<template>
  <div class="right-panel" :class="{ 'collapsed': !visible }">
    <!-- Header -->
    <div class="panel-header">
      <div class="header-left">
        <!-- <LazyIcon name="rightIcon" :size="15" style="margin-right: 5px;" /> -->
        <div class="header-text">
          <div class="panel-title truncate" :title="selectedNode ? '节点属性' : '属性面板'">{{ selectedNode ? '节点属性' : '属性面板' }}</div>
          <div class="panel-subtitle truncate" v-if="selectedNode" :title="selectedNode.label || selectedNode.type">{{ selectedNode.label || selectedNode.type }}</div>
        </div>
      </div>
      <div class="header-actions">
        <button class="header-btn" @click="confirmChanges" title="确认">
          <ElIcon><Check /></ElIcon>
        </button>
        <button class="header-btn" @click="cancelChanges" title="取消">
          <ElIcon><CloseBold /></ElIcon>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="panel-content" v-if="visible">
      <div v-if="!selectedNode" class="no-selection">
        <div class="no-selection-icon"><ElIcon><Aim /></ElIcon></div>
        <div class="no-selection-text">选择一个节点以查看其属性</div>
      </div>

      <div v-else class="property-sections">
        <!-- Overview -->
        <div class="overview-board">
          <div class="overview-card">
            <div class="overview-label">节点类型</div>
            <div class="overview-value truncate" :title="selectedNode.type">{{ selectedNode.type }}</div>
          </div>
          <div class="overview-card">
            <div class="overview-label">节点ID</div>
            <div 
              class="overview-value mono truncate clickable" 
              :title="selectedNode.id ? selectedNode.id + '（点击复制）' : ''"
              @click="copyNodeId"
              @keydown.enter="copyNodeId"
              tabindex="0"
              role="button"
            >{{ selectedNode.id }}</div>
          </div>
        </div>

        <!-- Config Slot -->
       
                <div class="section" v-if="!hideConfigSection">
        
        <div class="section-header">
          <LazyIcon name="nodeIcon" :size="13" style="margin-right: 5px;" />
          <span class="section-title">节点配置</span>
          <!-- <span class="section-badge editable">可编辑</span> -->
        </div>
        <div class="section-content">
          <slot name="config"
            :config="nodeConfig.config"
            :node="selectedNode"
            :setConfig="setConfig"
            :markDirty="markDirty"
          >
            <!-- 动态属性面板：优先渲染节点注册的 propertyPanel 组件 -->
            <component v-if="propertyPanelComp" :is="propertyPanelComp" />
            <div v-else class="no-config">
              <div class="no-config-text">未提供配置插槽 (name="config")，且当前节点未注册属性面板组件。</div>
            </div>
          </slot>
        </div>
        </div>

        <!-- Handles -->
        <div class="section handles-section">
          <div class="section-header">
            <LazyIcon name="linkIcon" :size="14" style="margin-right: 5px;" />
            <span class="section-title">连接点</span>
            <span class="section-badge readonly">只读</span>
          </div>
          <div class="section-content">
            <div class="handles-info">
              <div class="handle-group" v-if="nodeHandles.inputs?.length > 0">
                <div class="handle-type">
                  输入端口 <span class="count">{{ nodeHandles.inputs.length }}</span>
                </div>
                <div class="handle-list">
                  <div class="handle-item" v-for="handle in nodeHandles.inputs" :key="handle.id" :title="`${handle.name || handle.id} (${handle.id}) - ${handle.dataType || 'any'}`">
                    <div class="handle-name truncate">
                      <span class="cn">{{ handle.name || handle.id }}</span>
                      <span class="en">({{ handle.id }})</span>
                    </div>
                    <div class="handle-type-badge">{{ handle.dataType || 'any' }}</div>
                  </div>
                </div>
              </div>

              <div class="handle-group" v-if="nodeHandles.outputs?.length > 0">
                <div class="handle-type">
                  输出端口 <span class="count">{{ nodeHandles.outputs.length }}</span>
                </div>
                <div class="handle-list">
                  <div class="handle-item" v-for="handle in nodeHandles.outputs" :key="handle.id" :title="`${handle.name || handle.id} (${handle.id}) - ${handle.dataType || 'any'}`">
                    <div class="handle-name truncate">
                      <span class="cn">{{ handle.name || handle.id }}</span>
                      <span class="en">({{ handle.id }})</span>
                    </div>
                    <div class="handle-type-badge">{{ handle.dataType || 'any' }}</div>
                  </div>
                </div>
              </div>

              <div v-if="!nodeHandles.inputs?.length && !nodeHandles.outputs?.length" class="no-handles">
                此节点没有定义连接点
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Bar -->
    <div class="action-bar" v-if="visible && selectedNode">
      <div class="validation-status" :class="validationStatus.type">
        <span class="status-icon">
          <ElIcon v-if="validationStatus.type === 'success'"><CircleCheck /></ElIcon>
          <ElIcon v-else-if="validationStatus.type === 'error'"><CircleClose /></ElIcon>
          <ElIcon v-else><QuestionFilled /></ElIcon>
        </span>
        {{ validationStatus.message }}
      </div>
      <button 
        class="apply-btn" 
        :class="{ 'dirty': isDirty, 'disabled': !canApply }"
        :disabled="!canApply"
        @click="applyChanges"
      >
        <span class="btn-icon">
          <ElIcon v-if="isDirty"><EditPen /></ElIcon>
          <ElIcon v-else><Check /></ElIcon>
        </span>
        {{ isDirty ? '应用更改' : '已应用' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, computed, watch, provide, defineAsyncComponent } from 'vue'
import { useWorkflowStore } from 'mflows'
import { Aim, CircleCheck, CircleClose, QuestionFilled, EditPen, Check, CloseBold, ElIcon, ElMessage } from '@/plugins/elementPlus'
import { cloneDeep } from 'es-toolkit/compat'
import nodeManager from '@components/flow/services/nodeManager'
import { LazyIcon } from '@/exports/components'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ toggle: [] }>()

const workflow = useWorkflowStore()

// 为属性面板子组件提供上下文（兼容 CustomFunctionDrawer.vue / propertyPanel.vue 等）
provide('nodeManager', nodeManager)
provide('findNode', workflow.findNode)
provide('updateNode', workflow.updateNode)
provide('addNode', workflow.addNode)
provide('updateNodeData', workflow.updateNodeData)

// 当前选中的节点
const selectedNode = computed(() => {
  const activeNodeId = workflow.state.activeNodeId
  if (!activeNodeId) return null
  return workflow.findNode(activeNodeId)
})

// 当选中节点为输入类节点时隐藏右侧“节点配置”区块（由 nodeManager 类别判断）
const hideConfigSection = computed(() => {
  const t = selectedNode.value?.type
  if (!t) return false
  const reg = nodeManager.getNodeConfig(t)
  // 仅当明确为 null（而非 undefined）时隐藏
  return reg?.propertyPanel === null
})

// 动态属性面板组件（来自 nodeManager 注册） —— 使用 shallowRef 避免将组件对象变为响应式
const propertyPanelComp = shallowRef<any | null>(null)

async function loadPropertyPanel(type: string) {
  try {
    const panel = nodeManager.getPropertyPanel(type)
    if (!panel) { propertyPanelComp.value = null; return }

    // 情况1：已经是组件（包括 defineAsyncComponent 返回的异步组件）
    if (typeof panel === 'function') {
      // defineAsyncComponent 会带有 __asyncLoader 标记，或本身就是可渲染组件函数
      if ((panel as any).__asyncLoader || (panel as any).setup || (panel as any).render) {
        propertyPanelComp.value = panel as any
      } else {
        // 情况2：传入的是一个() => Promise<Component> 的工厂，包装为异步组件
        propertyPanelComp.value = defineAsyncComponent(panel as any)
      }
    } else {
      propertyPanelComp.value = panel
    }
  } catch (e) {
    console.warn('加载属性面板失败:', e)
    propertyPanelComp.value = null
  }
}

// 节流/防抖工具
let realtimeTimer: number | null = null
function pushRealtimeUpdate() {
  if (!selectedNode.value) return
  // 200ms 防抖，合并连续输入
  if (realtimeTimer) window.clearTimeout(realtimeTimer)
  realtimeTimer = window.setTimeout(() => {
    if (!selectedNode.value) return
    try {
      workflow.updateNodePropertyPanel(selectedNode.value.id, cloneDeep(nodeConfig.value.config))
    } catch (e) {
      // 安静失败，避免打断输入
      console.warn('realtime update failed', e)
    }
  }, 200)
}

// 节点配置数据
const nodeConfig = ref({
  label: '',
  config: {} as any
})

// 节点连接点信息
const nodeHandles = computed(() => {
  if (!selectedNode.value?.data?.handles) {
    return { inputs: [], outputs: [] }
  }
  return selectedNode.value.data.handles
})

// 应用状态
const isDirty = ref(false)
const validationStatus = ref({
  type: 'idle' as 'idle' | 'success' | 'error' | 'warning',
  message: '等待验证'
})

// 能否应用更改
const canApply = computed(() => {
  return selectedNode.value && (isDirty.value || validationStatus.value.type === 'error')
})

// 监听选中节点变化
watch(selectedNode, (newNode) => {
  if (newNode) {
    // 重置配置
    const baseCfg = (newNode.data?.propertyPanel && Object.keys(newNode.data.propertyPanel).length
      ? newNode.data.propertyPanel
      : newNode.data?.config) || {}
    nodeConfig.value = {
      label: newNode.label || '',
      config: { ...baseCfg }
    }
    isDirty.value = false
    validateConfiguration()
    // 清理实时计时器
    if (realtimeTimer) { window.clearTimeout(realtimeTimer); realtimeTimer = null }
    // 加载属性面板组件
    loadPropertyPanel(newNode.type)
  } else {
    // 清空配置
    nodeConfig.value = { label: '', config: {} }
    isDirty.value = false
    validationStatus.value = { type: 'idle', message: '等待验证' }
    propertyPanelComp.value = null
  }
 }, { immediate: true })

// 当子属性面板通过 store.updateNodePropertyPanel 写入实时数据时，标记为脏并触发校验
watch(() => selectedNode.value?.data?.propertyPanel, (val) => {
  if (!selectedNode.value) return
  isDirty.value = true
  // 同步到本地配置，确保“应用更改”以最新实时数据为准
  if (val && typeof val === 'object') {
    try { nodeConfig.value.config = cloneDeep(val as any) } catch {}
  }
  setTimeout(validateConfiguration, 200)
}, { deep: true })

// 标记为已修改
 function markDirty() {
   isDirty.value = true
   // 实时推送到容器以更新节点内部表单
   pushRealtimeUpdate()
   // 延迟验证
   setTimeout(validateConfiguration, 300)
 }
 

// 配置校验：避免未定义函数导致运行时报错，并给出直观状态
function validateConfiguration() {
  const node = selectedNode.value
  if (!node) {
    validationStatus.value = { type: 'idle', message: '等待验证' }
    return
  }

  try {
    // 优先使用实时的 propertyPanel，其次使用本地 nodeConfig
    const cfg = (node.data?.propertyPanel && Object.keys(node.data.propertyPanel).length
      ? node.data.propertyPanel
      : nodeConfig.value.config) || {}

    // 简单校验：是否存在可用配置键
    const hasKeys = cfg && typeof cfg === 'object' && Object.keys(cfg).length > 0
    if (hasKeys) {
      validationStatus.value = { type: 'success', message: '配置验证通过' }
    } else {
      validationStatus.value = { type: 'warning', message: '尚未配置任何参数' }
    }
  } catch (e) {
    console.warn('validateConfiguration error:', e)
    validationStatus.value = { type: 'error', message: '配置校验出错' }
  }
}

 
 
 function applyChanges() {
   if (!selectedNode.value || !canApply.value) return
 
   try {
     // 更新节点数据
     workflow.updateNode(selectedNode.value.id, {
       label: nodeConfig.value.label
     })

     // 以 propertyPanel 为主（若有），否则使用本地 config
     const panel = selectedNode.value.data?.propertyPanel as any
     const payload = cloneDeep(panel && Object.keys(panel).length ? panel : nodeConfig.value.config)

     workflow.updateNodeData(selectedNode.value.id, {
       config: payload,
       // 写入 propertyPanel 以触发容器内监听
       propertyPanel: cloneDeep(payload)
     })

     // 保存快照
     workflow.saveSnapshot()

     // 重置状态并同步本地配置
     nodeConfig.value.config = cloneDeep(payload)
     isDirty.value = false
     validationStatus.value = { type: 'success', message: '更改已应用并保存快照' }

     setTimeout(() => {
       validateConfiguration()
     }, 2000)
   } catch (error) {
     validationStatus.value = { type: 'error', message: '应用更改失败' }
     console.error('Apply changes error:', error)
   }
 }

// 新增：确认与取消操作
function confirmChanges() {
  // 直接复用现有的应用逻辑
  applyChanges()
  // 关闭面板
  emit('toggle')
}

function cancelChanges() {
  if (!selectedNode.value) return
  // 还原为当前节点数据，放弃未保存的更改
  nodeConfig.value = {
    label: selectedNode.value.label || '',
    config: selectedNode.value.data?.config ? { ...selectedNode.value.data.config } : {}
  }
  isDirty.value = false
  // 提示已取消，随后恢复到校验状态
  validationStatus.value = { type: 'idle', message: '更改已取消' }
  setTimeout(() => {
    validateConfiguration()
  }, 1000)
  // 关闭面板
  emit('toggle')
}

// 新增：供外部插槽设置配置（整体替换或函数式更新）
function setConfig(next: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) {
  try {
    if (typeof next === 'function') {
      const updated = (next as (prev: Record<string, any>) => Record<string, any>)({ ...nodeConfig.value.config })
      nodeConfig.value.config = updated || {}
    } else if (next && typeof next === 'object') {
      nodeConfig.value.config = next
    } else {
      return
    }
    markDirty()
  } catch (e) {
    console.warn('setConfig failed:', e)
  }
}

// 新增：点击复制节点ID
async function copyNodeId() {
  if (!selectedNode.value?.id) return
  const id = selectedNode.value.id
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(id)
    } else {
      // 兼容性降级
      const textarea = document.createElement('textarea')
      textarea.value = id
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    ElMessage.success('已复制节点 ID')
  } catch (e) {
    console.warn('复制失败', e)
    ElMessage.error('复制失败，请重试')
  }
}
</script>

<style scoped lang="scss">
/* 容器与头部 */
.right-panel {
  /* 统一面板变量，和 BottomPanel 对齐 */
  --panel-bg: #202020;
  --panel-border: #3a3a3a;
  --panel-text: #ADADAB;
  --panel-text-secondary: #999;
  --panel-accent: #007acc;
  --panel-hover-bg: #333;
  --panel-surface: #2b2b2b;
  --panel-radius: 2px;
  --panel-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  position: fixed;
  right: 0;
  top: 40px;
  width: 320px; // 调整为 320，与全局默认一致
  height: 100vh;
  background: var(--panel-bg);
  border-left: 1px solid var(--panel-border);
  box-shadow: var(--panel-shadow);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  &.collapsed { transform: translateX(100%); border-left-color: transparent; box-shadow: none; }
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: var(--panel-bg);
  border-bottom: 1px solid var(--panel-border);
  min-height: 42px;
  font-size: 14px;
}
.header-left { display: flex; align-items: center; gap: 8px; }
.header-text { display: flex; flex-direction: column; min-width: 0; }
.panel-subtitle { font-size: 10px; color: var(--panel-text); line-height: 1.2; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* 连接点名称截断 */
.handle-name { flex: 1 1 auto; min-width: 0; }
.header-actions { display: flex; align-items: center; gap: 4px; }
.header-btn { background: none; border: 1px solid var(--panel-border); color: var(--panel-text-secondary); cursor: pointer; padding: 4px; border-radius: 4px; font-size: 11px; transition: all 0.2s; &:hover { background: var(--panel-hover-bg); color: var(--panel-text); border-color: var(--panel-accent); } }

/* 内容区与空态 */
.panel-content { flex: 1; overflow-y: auto; padding: 12px; }
.no-selection { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 140px; color: #666; }
.no-selection-icon { font-size: 28px; margin-bottom: 10px; }
.no-selection-text { font-size: 13px; text-align: center; }

/* 区块与标题行（尽量复用全局 .section 样式，仅做必要补充） */
.section { margin-bottom: 12px; border-radius: var(--panel-radius); overflow: hidden; }
.section-header { padding: 8px 12px; background: var(--panel-surface); border-bottom: 1px solid var(--panel-border); display: flex; align-items: center; justify-content: flex-start; }
.section-title { color: var(--panel-text); font-size: 12px; font-weight: 500; }
.section-content { padding: 8px 12px; background-color: rgba($color:#141414, $alpha: 0.96)}
/* 徽标：可编辑/只读 */
.section-badge { padding: 2px 6px; border-radius: 10px; font-size: 10px; line-height: 1; opacity: 0.9; &.editable { background: var(--panel-accent); color: #fff; } &.readonly { background: #3a3a3a; color: #bbb;margin-left: auto; } }
.section.readonly { background: #232323; }

/* 表单控件 */
.property-group { margin-bottom: 12px; &:last-child { margin-bottom: 0; } }
.property-label { display: block; color: var(--panel-text); font-size: 11px; font-weight: 500; margin-bottom: 4px; }
.property-input { width: 100%; padding: 6px 10px; background: var(--panel-hover-bg); border: 1px solid var(--panel-border); border-radius: 4px; color: var(--panel-text); font-size: 11px; transition: border-color 0.2s; &:focus { outline: none; border-color: var(--panel-accent); } &:disabled { opacity: 0.6; cursor: not-allowed; } &::placeholder { color: #666; } }
.checkbox-label { display: flex; align-items: center; gap: 6px; color: var(--panel-text); font-size: 11px; cursor: pointer; input[type="checkbox"] { margin: 0; } }

/* 插槽缺省提示 */
.no-config { text-align: center; padding: 20px; color: #666; }
.no-config-text { font-size: 12px; }

/* 新增：概览看板（只读） */
.overview-board { display: grid; grid-template-columns: 1fr; gap: 6px; margin-bottom: 8px; }
.overview-card { display: flex; align-items: center; justify-content: space-between; background: var(--panel-surface); border: 1px solid var(--panel-border); border-radius: 2px; padding: 6px; }
.overview-label { color: var(--panel-text-secondary); font-size: 12px; margin-bottom: 4px; letter-spacing: 0.2px; }
.overview-value { color: var(--panel-text); font-size: 12px; font-weight: 600; word-break: break-all; text-align: right; max-width: 70%; }
.overview-value.mono { font-family: 'Courier New', monospace; }

/* 连接点展示（紧凑 chip 排版） */
.handles-info { display: flex; flex-direction: column; gap: 10px; }
.handle-group .handle-type { color: var(--panel-text-secondary); font-size: 10px; font-weight: 500; margin: 2px 0 4px; }
.handle-list { display: flex; flex-wrap: wrap; gap: 6px; }
.handle-item { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px; background: var(--panel-hover-bg); border: 1px solid var(--panel-border); border-radius: 999px; max-width: 100%; }
.handle-name { display: inline-flex; align-items: baseline; gap: 2px; color: var(--panel-text); font-size: 11px; max-width: 140px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.handle-type-badge { background: var(--panel-accent); color: #fff; padding: 1px 6px; border-radius: 999px; font-size: 10px; font-weight: 600; line-height: 1.6; }
.no-handles { text-align: center; color: #666; font-size: 12px; padding: 8px; }
/* 覆盖：概览单列显示，每行一个 */


/* 底部操作栏与状态样式 */
.action-bar { 
  padding: 8px 12px; 
  border-top: 1px solid var(--panel-border); 
  background: var(--panel-bg); 
  display: flex; 
  align-items: center; 
  justify-content: space-between; 
}
.validation-status { 
  display: inline-flex; 
  align-items: center; 
  gap: 6px; 
  color: var(--panel-text); 
  font-size: 12px; 
}
.validation-status.success { color: #9be28c; }
.validation-status.error { color: #ff8a8a; }
.validation-status.warning { color: #e9d26b; }
.validation-status.idle { color: var(--panel-text-secondary); }

.apply-btn {
  background: var(--panel-accent);
  border: none;
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.apply-btn.disabled, .apply-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.apply-btn.dirty { box-shadow: 0 0 0 2px rgba(0,122,204,0.25) inset; }

/* 连接点：中英文名同时展示 */
.handle-name { display: inline-flex; align-items: baseline; gap: 2px; }
.handle-name .cn { color: var(--panel-text); font-size: 11px; }
.handle-name .en { color: var(--panel-text-secondary); font-size: 10px; margin-left: 2px; }
.overview-value.clickable { cursor: pointer; }
</style>
